'use client';

// App-wide UI click sounds. Played by a single delegated `click` listener so a sound
// fires without touching hundreds of onClick handlers (a press-and-scroll is NOT a
// click, so it stays silent). Any interactive tap plays the default menu-select sound;
// a data-sound marker only overrides the variant:
//   • default (or data-sound="menu_select") → 'menu_select' — every interactive tap
//   • звонкий (data-sound="ring")            → 'click' — bottom tab bar + RushMid
//                lobby and buy (закуп) screens
//   • CTA (data-sound="cta")                 → 'buttonclickrelease' — big sound on
//                primary actions: case open, upgrade, contract, case-battle, buy-coins
// Interactivity is still required (button/a/role or computed cursor:pointer), so a tap
// on empty background stays silent. data-sound="none" opts an element out entirely.

import { loadSounds, playSfx, wireUnlock } from './audioEngine';

const UI_SFX = ['click', 'buttonclickrelease', 'menu_select', 'click_wheel'] as const;
let wired = false;

/** Decode the three click sounds (call once, app-wide). */
export function preloadUiSounds(): void {
  void loadSounds(UI_SFX).catch(() => undefined);
}

/** Reward chime — play whenever the user receives money (claim a reward, sell an
 *  item). Loads on demand, then plays; a no-op if it can't decode. */
export function playEarnings(volume = 0.6): void {
  void loadSounds(['earnings']).then(() => playSfx('earnings', volume)).catch(() => undefined);
}

const isInteractive = (el: Element): boolean => {
  const tag = el.tagName;
  if (tag === 'BUTTON' || tag === 'A' || tag === 'SUMMARY' || tag === 'INPUT' || tag === 'LABEL') return true;
  const role = el.getAttribute('role');
  if (role === 'button' || role === 'link' || role === 'tab' || role === 'menuitem' || role === 'switch') return true;
  try { if (window.getComputedStyle(el).cursor === 'pointer') return true; } catch { /* detached */ }
  return false;
};

const isDisabled = (el: Element): boolean =>
  (el as HTMLButtonElement).disabled === true || el.getAttribute('aria-disabled') === 'true';

/** Wire the single global click-sound listener (idempotent).
 *  Uses the `click` event, NOT pointerdown: the browser only fires `click` on a
 *  genuine tap — a press that turns into a scroll/swipe is suppressed — so pressing
 *  to scroll a list (e.g. ItemListItem) or swipe a row does NOT play a sound. (All
 *  scrolling in the app is native overflow, so the browser's own click-cancel covers
 *  it; no manual drag tracking needed.) */
export function initClickSound(): void {
  if (wired || typeof document === 'undefined') return;
  wired = true;
  wireUnlock();
  document.addEventListener('click', (e) => {
    const target = e.target as Element | null;
    if (!target) return;

    // Nearest ancestor (incl self) carrying a data-sound marker overrides the variant —
    // unbounded so a marker on a screen root still wins over the bounded scan below.
    // No marker → the default menu-select sound.
    const marker = target.closest('[data-sound]');
    const ds = marker?.getAttribute('data-sound');
    if (ds === 'none') return; // explicit opt-out
    const cta = ds === 'cta';
    const ring = ds === 'ring';

    // Nearest interactive element (button/link/role/pointer) decides whether the tap
    // makes a sound at all — empty-background taps stay silent.
    let interactiveEl: Element | null = null;
    for (let el: Element | null = target, i = 0; el && i < 8; i++, el = el.parentElement) {
      if (isInteractive(el)) { interactiveEl = el; break; }
    }
    if (!interactiveEl && cta && marker) interactiveEl = marker;
    if (!interactiveEl || isDisabled(interactiveEl)) return;

    const sound = cta ? 'buttonclickrelease' : ring ? 'click' : 'menu_select';
    playSfx(sound, cta ? 1 : 0.5);
  }, true);
}
